/** @jsxImportSource @emotion/react */

import React from 'react';
import { v4 as uuid4 } from 'uuid';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import * as text from '@constant/text';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { useProvidersQuery, useArticlesQuery } from '@hook/moashelf/useApi';
import { removeEmailDomain } from '@util/string';
import { IArticleData, IProviderData } from '@datatype/moashelf/api-type';
import TextBlock from '@component/common/block/text-block';
import ArticleItem from '@component/moashelf/newsletter/begin/article-item';
import ProviderItem from '@component/moashelf/newsletter/begin/provider-item';
import BeginCategory from '@component/moashelf/newsletter/begin/begin-category';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import Line from '@component/common/line';

const Wrapper = styled.div`
  height: 100%;
`;

const ItemsContainer = styled.div`
  //width: calc(100% - 2 * ${size.MARGIN_2});
  width: 100%;
  margin: 0 auto 0 auto;
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_3};
  border-radius: ${size.BORDER_RADIUS_3};
  padding: 0 ${size.MARGIN_1} 0 ${size.MARGIN_1};
`;

const TITLE_TEXT_DESIGN = {
  text: { size: IS_IYAGIGO_MOBILE_VIEW ? 'medium' : 'medium', color: color.BLACK_2, bold: true },
  box: { width: '100%', margin: `${size.MARGIN_3} 0 5px 0`, alignX: 'left' },
} as const;

const BeginToday: React.FC<any> = () => {
  const dispatch = useTypedDispatch();
  const categoryKey = useTypedSelector((state) => state.rootReducer?.newsletterReducer?.categoryKey) || null;
  const providersQuery = useProvidersQuery(categoryKey, 1, 10);
  const articlesQuery = useArticlesQuery(categoryKey, 1, 10);

  const articles = (articlesQuery.data?.data || []) as IArticleData[];
  const providers = (providersQuery.data?.data || []) as IProviderData[];

  const ArticleItems = articles.slice(0, 5).map((shelf) => {
    return (
      <ArticleItem
        key={uuid4()}
        articleKey={shelf.key}
        title={shelf.title}
        publishedAt={shelf.publishedAt}
        provider={shelf.provider.name || removeEmailDomain(shelf.provider.email)}
      />
    );
  });

  const ProviderItems = providers.slice(0, 5).map((provider) => {
    return (
      <ProviderItem
        key={uuid4()}
        providerKey={provider.key}
        name={provider.name ?? null}
        email={provider.email}
        description={provider.description || text.MOASHELF.PROVIDER_DESCRIPTION_NOTIFICATION}
        subscriptionPage={provider.subscriptionPage || text.MOASHELF.SUBSCRIPTION_PAGE_NOTIFICATION}
        publishDays={provider.publishDays || []}
      />
    );
  });

  return (
    <Wrapper>
      <BeginCategory />
      <TextBlock text={'많이 읽은 글'} design={TITLE_TEXT_DESIGN} />
      {ArticleItems.length > 0 ? (
        <ItemsContainer>{ArticleItems}</ItemsContainer>
      ) : (
        <>
          <Line color={color.GRAY_3} />
          <TextBlock
            text={'많이 읽은 글이 없습니다 🥲'}
            design={{ ...TITLE_TEXT_DESIGN, text: { ...TITLE_TEXT_DESIGN.text, bold: false } }}
          />
        </>
      )}
      <TextBlock text={'많이 보는 뉴스레터'} design={TITLE_TEXT_DESIGN} />
      {ProviderItems.length > 0 ? (
        <ItemsContainer>{ProviderItems}</ItemsContainer>
      ) : (
        <>
          <Line color={color.GRAY_3} />
          <TextBlock
            text={'많이 보는 뉴스레터가 없습니다 🥲'}
            design={{ ...TITLE_TEXT_DESIGN, text: { ...TITLE_TEXT_DESIGN.text, bold: false } }}
          />
        </>
      )}
    </Wrapper>
  );
};

export default BeginToday;
